/**
 * ESN-33 A/B Test: Newsletter Popup
 * -----------------------------------------------------------------------------
 * Triggers Klaviyo newsletter popup after 5 seconds for users in the test variation.
 *
 * Experiment ID: 358547
 * Variation ID: 1233433
 * Form ID: YqsM3j
 */

import { waitForKameleoon } from '~/helpers/kameleoon';

const ESN_33_CONFIG = {
  experimentId: 358547,
  variationId: 1233433,
  klaviyoFormId: 'YqsM3j',
  delay: 5000,
  // sessionStorage key to track if popup was shown (resets on new tab/browser session)
  storageKey: 'esn33PopupShown',
};

let esn33Cleanup = null;
let esn33Timeout = null;

/**
 * Check if popup was already shown in current session
 * @private
 * @returns {Boolean} True if popup was already shown
 */
function esn33WasPopupShown() {
  try {
    return sessionStorage.getItem(ESN_33_CONFIG.storageKey) === 'true';
  } catch (error) {
    // sessionStorage might be disabled
    return false;
  }
}

/**
 * Mark popup as shown in current session
 * @private
 */
function esn33MarkPopupAsShown() {
  try {
    sessionStorage.setItem(ESN_33_CONFIG.storageKey, 'true');
  } catch (error) {
    // Silently fail if sessionStorage is disabled
    if (cnvs.environment.mode === 'development') {
      // eslint-disable-next-line no-console
      console.warn(
        '[ESN-33] Could not save popup state to sessionStorage',
        error,
      );
    }
  }
}

/**
 * Wait for Klaviyo to be ready
 * @private
 * @param {Function} callback - Called when Klaviyo is ready
 */
function esn33WaitForKlaviyo(callback) {
  const maxAttempts = 50; // 5 seconds max (50 * 100ms)
  let attempts = 0;

  const checkKlaviyo = setInterval(() => {
    attempts++;

    // Check if Klaviyo is ready
    // eslint-disable-next-line no-underscore-dangle
    const klaviyoReady = typeof window._klOnsite !== 'undefined';

    if (klaviyoReady || attempts >= maxAttempts) {
      clearInterval(checkKlaviyo);

      if (klaviyoReady) {
        if (cnvs.environment.mode === 'development') {
          // eslint-disable-next-line no-console
          console.log('[ESN-33] Klaviyo is ready');
        }
        callback(true);
      } else {
        if (cnvs.environment.mode === 'development') {
          // eslint-disable-next-line no-console
          console.warn('[ESN-33] Klaviyo not loaded after timeout');
        }
        callback(false);
      }
    }
  }, 100);
}

/**
 * Trigger Klaviyo popup
 * @private
 */
function esn33TriggerKlaviyoPopup() {
  // Check if popup was already shown
  if (esn33WasPopupShown()) {
    if (cnvs.environment.mode === 'development') {
      // eslint-disable-next-line no-console
      console.log('[ESN-33] Popup already shown, skipping');
    }
    return;
  }

  if (cnvs.environment.mode === 'development') {
    // eslint-disable-next-line no-console
    console.log('[ESN-33] Waiting for Klaviyo to be ready...');
  }

  // Wait for Klaviyo to be ready before triggering
  esn33WaitForKlaviyo((isReady) => {
    if (!isReady) {
      if (cnvs.environment.mode === 'development') {
        // eslint-disable-next-line no-console
        console.error('[ESN-33] Cannot trigger popup - Klaviyo not available');
      }
      return;
    }

    // eslint-disable-next-line no-underscore-dangle
    if (typeof window._klOnsite === 'undefined') {
      // eslint-disable-next-line no-underscore-dangle
      window._klOnsite = [];
    }

    // eslint-disable-next-line no-underscore-dangle
    window._klOnsite.push(['openForm', ESN_33_CONFIG.klaviyoFormId]);

    // Mark popup as shown
    esn33MarkPopupAsShown();

    if (cnvs.environment.mode === 'development') {
      // eslint-disable-next-line no-console
      console.log('[ESN-33] Klaviyo popup triggered and marked as shown');
    }
  });
}

/**
 * Initialize ESN-33 A/B test
 * Waits for Kameleoon, checks if user is in test variation,
 * then triggers Klaviyo popup after delay
 */
export function initESN33NewsletterPopup() {
  // Skip on checkout pages
  if (cnvs.page.type === 'checkout') {
    if (cnvs.environment.mode === 'development') {
      // eslint-disable-next-line no-console
      console.log('[ESN-33] Skipping on checkout page');
    }
    return;
  }

  if (cnvs.environment.mode === 'development') {
    // eslint-disable-next-line no-console
    console.log(
      '[ESN-33] Initializing newsletter popup test',
      `\nExperiment ID: ${ESN_33_CONFIG.experimentId}`,
      `\nVariation ID: ${ESN_33_CONFIG.variationId}`,
      `\nDelay: ${ESN_33_CONFIG.delay}ms`,
    );
  }

  // Wait for Kameleoon to load and check variation
  esn33Cleanup = waitForKameleoon({
    experimentId: ESN_33_CONFIG.experimentId,
    variationId: ESN_33_CONFIG.variationId,
    onReady: (isInTestVariation, experiment) => {
      if (cnvs.environment.mode === 'development') {
        // eslint-disable-next-line no-console
        console.log(
          '[ESN-33] Kameleoon ready',
          '\nisInTestVariation:',
          isInTestVariation,
          '\nExperiment:',
          experiment,
        );
      }

      if (isInTestVariation) {
        if (cnvs.environment.mode === 'development') {
          // eslint-disable-next-line no-console
          console.log(
            `[ESN-33] User is in test variation, will trigger popup in ${ESN_33_CONFIG.delay}ms`,
          );
        }

        // Trigger popup after delay
        esn33Timeout = setTimeout(() => {
          esn33TriggerKlaviyoPopup();
        }, ESN_33_CONFIG.delay);
      } else if (cnvs.environment.mode === 'development') {
        // eslint-disable-next-line no-console
        console.log('[ESN-33] User is not in test variation');
      }
    },
  });
}

/**
 * Cleanup ESN-33 test timers and listeners
 * Call this if component unmounts or page changes
 */
export function cleanupESN33NewsletterPopup() {
  if (esn33Cleanup) {
    esn33Cleanup();
    esn33Cleanup = null;
  }

  if (esn33Timeout) {
    clearTimeout(esn33Timeout);
    esn33Timeout = null;
  }
}

/**
 * Force trigger the popup (for testing)
 * Usage in browser console: window.esn33ForcePopup()
 */
export function esn33ForcePopup() {
  // eslint-disable-next-line no-console
  console.log('[ESN-33] Force triggering popup...');

  // Clear sessionStorage to allow popup
  try {
    sessionStorage.removeItem(ESN_33_CONFIG.storageKey);
  } catch (error) {
    // Ignore
  }

  esn33TriggerKlaviyoPopup();
}

// Expose debug functions globally
if (typeof window !== 'undefined') {
  window.esn33ForcePopup = esn33ForcePopup;
  window.esn33CheckStatus = () => {
    const wasShown = esn33WasPopupShown();
    // eslint-disable-next-line no-console
    console.log('[ESN-33] Status:', {
      popupAlreadyShown: wasShown,
      config: ESN_33_CONFIG,
      hasKameleoon: typeof window.Kameleoon !== 'undefined',
      // eslint-disable-next-line no-underscore-dangle
      hasKlaviyoOnsite: typeof window._klOnsite !== 'undefined',
    });
  };
  window.esn33ResetAndReload = () => {
    try {
      sessionStorage.removeItem(ESN_33_CONFIG.storageKey);
      // eslint-disable-next-line no-console
      console.log('[ESN-33] Session cleared. Reloading page...');
      window.location.reload();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[ESN-33] Could not reset and reload:', error);
    }
  };
}
